// @section 1 Campaigns / Trend — calendar-date trend, TRUE per-day activity
// Reads analytics.v_campaign_master_daily_activity via /api/hq/campaigns/
// masters/{key}/daily. No client-side differencing — these are real per-day
// counts keyed by event date, so demo_clicks / diag_visits are trustworthy.
// One chart, metric toggle (counts + rates), daily/weekly/monthly granularity.

import { useEffect, useMemo, useState } from 'react';
import TrendChart, { type TrendMetric, type TrendRow } from '../../components/TrendChart';
import {
  getCampaignMastersOverview,
  getCampaignMasterDaily,
  CampaignsUnconfigured,
  type CampaignMasterRow,
  type CampaignDailyActivity,
} from '../../api';

// @section 2 Metric definitions for the chart toggle
const BASE_FIELDS = ['sent', 'delivered', 'bounced', 'opens', 'demo', 'diag', 'filled', 'booked', 'replies'];
const METRICS: TrendMetric[] = [
  { key: 'sent', label: 'Sent', kind: 'count', field: 'sent', color: '#14b8a6' },
  { key: 'opens', label: 'Opens', kind: 'count', field: 'opens', color: '#10b981' },
  { key: 'demo', label: 'Demo clicks', kind: 'count', field: 'demo', color: '#f59e0b' },
  { key: 'diag', label: 'Diag visits', kind: 'count', field: 'diag', color: '#a78bfa' },
  { key: 'filled', label: 'Filled', kind: 'count', field: 'filled', color: '#c084fc' },
  { key: 'booked', label: 'Booked', kind: 'count', field: 'booked', color: '#f0606b' },
  { key: 'openRate', label: 'Open rate', kind: 'rate', num: 'opens', den: 'delivered', color: '#10b981' },
  { key: 'bounceRate', label: 'Bounce rate', kind: 'rate', num: 'bounced', den: 'sent', color: '#f0606b' },
  { key: 'replyRate', label: 'Reply rate', kind: 'rate', num: 'replies', den: 'delivered', color: '#14b8a6' },
];

// @section 3 Main
export default function Trend() {
  const [masters, setMasters] = useState<CampaignMasterRow[] | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [daily, setDaily] = useState<CampaignDailyActivity[] | null>(null);
  const [masterName, setMasterName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);

  // @subsection 3.1 Load masters + default to biggest
  useEffect(() => {
    getCampaignMastersOverview()
      .then((d) => { setMasters(d.masters); if (d.masters[0]) setSelectedKey(d.masters[0].master_key); })
      .catch((e: unknown) => {
        if (e instanceof CampaignsUnconfigured) setUnconfigured(true); else setErr(String(e));
      });
  }, []);

  // @subsection 3.2 Load daily activity (120d)
  useEffect(() => {
    if (!selectedKey) return;
    setDaily(null);
    setMasterName(masters?.find((m) => m.master_key === selectedKey)?.master_name ?? '');
    getCampaignMasterDaily(selectedKey, 120)
      .then(setDaily)
      .catch((e: unknown) => {
        if (e instanceof CampaignsUnconfigured) setUnconfigured(true); else setErr(String(e));
      });
  }, [selectedKey, masters]);

  // @subsection 3.3 Map daily activity → TrendChart rows (add derived `delivered`)
  const rows = useMemo<TrendRow[]>(() => {
    if (!daily) return [];
    return daily.map((d) => ({
      date: d.activity_date.slice(0, 10),
      sent: d.sent, bounced: d.bounced, delivered: Math.max(d.sent - d.bounced, 0),
      opens: d.opens, demo: d.demo_clicks, diag: d.diag_visits, filled: d.filled ?? 0,
      booked: d.booked, replies: d.replies,
    }));
  }, [daily]);

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1600px] mx-auto">

          {/* @subsection 3.4 Breadcrumb + selector */}
          <div className="flex items-center justify-between text-xs mb-8 flex-wrap gap-3">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Campaigns</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">Trend</span>
            </div>
            {masters && masters.length > 0 && (
              <select value={selectedKey ?? ''} onChange={(e) => setSelectedKey(e.target.value)}
                className="bg-ink-900 border border-ink-700 rounded px-3 py-1.5 text-paper-100 text-xs">
                {masters.map((m) => (
                  <option key={m.master_key} value={m.master_key}>
                    {m.master_name} · {m.sent.toLocaleString()} sent
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* @subsection 3.5 Title */}
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">Daily trend · per-day activity</div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100">
              {masterName || (unconfigured ? 'Pipeline not configured' : 'Loading…')}
            </h1>
          </div>

          {err && <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 mb-6 text-stall text-sm">{err}</div>}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 mb-6 text-sm text-watch">
              Backend returned 503 — awaiting first refresh.
            </div>
          )}

          {/* @subsection 3.6 The trend chart */}
          {rows.length >= 2 && (
            <TrendChart data={rows} metrics={METRICS} baseFields={BASE_FIELDS} title="Daily performance" />
          )}

          {!daily && !err && !unconfigured && (
            <div className="text-muted text-sm py-20 text-center">Loading trend…</div>
          )}
          {daily && rows.length < 2 && (
            <div className="text-muted text-sm py-20 text-center">Not enough daily data for this campaign yet.</div>
          )}

        </div>
      </section>
    </div>
  );
}
